import { lazy, Suspense, useEffect, useState } from "react";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { downloadEventAttachment, eventAttachmentUrl } from "../../services/api";
import { preparePrivateMedia } from "../../utils/privateMedia";

const PdfAttachmentView = lazy(() => import("./PdfAttachmentView").then(module => ({ default: module.PdfAttachmentView })));

export const EventAttachmentPreview = ({ attachment, eventId, onClose, getContent = downloadEventAttachment, getContentUrl = eventAttachmentUrl }) => {
  const [source, setSource] = useState("");
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const mime = attachment?.content_type || attachment?.file?.type || "";
  useEffect(() => {
    if (!attachment) return;
    let active = true, objectUrl;
    const controller = new AbortController();
    setSource(""); setError("");
    const load = async () => {
      try {
        if (attachment.file) objectUrl = URL.createObjectURL(attachment.file);
        else if (mime.startsWith("video/") || mime.startsWith("audio/")) {
          await preparePrivateMedia();
          if (active) setSource(getContentUrl(eventId, attachment.id));
          return;
        } else {
          const { data } = await getContent(eventId, attachment.id, controller.signal);
          if (!active) return;
          objectUrl = URL.createObjectURL(data);
        }
        if (active) setSource(objectUrl);
      } catch (err) { if (active && err.code !== "ERR_CANCELED") setError(err.response?.status === 404 ? "Fichier introuvable ou accès non autorisé." : err.message || "Impossible de charger le fichier."); }
    };
    load();
    return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment, eventId, mime, getContent, getContentUrl]);
  const download = async () => {
    setDownloading(true);
    try {
      const blob = attachment.file || (await getContent(eventId, attachment.id)).data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = attachment.filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Téléchargement impossible"); }
    finally { setDownloading(false); }
  };
  return <Dialog open={!!attachment} onOpenChange={open => !open && onClose()}>
    <DialogContent className="responsive-dialog max-w-3xl max-h-[94dvh] overflow-y-auto" data-testid="event-file-preview" onOpenAutoFocus={event => event.preventDefault()}>
      <DialogHeader><DialogTitle className="pr-7 text-base text-left break-words [overflow-wrap:anywhere]" data-testid="event-file-preview-name">{attachment?.filename}</DialogTitle></DialogHeader>
      {!source && !error && <div className="py-12 flex justify-center" data-testid="event-file-preview-loading"><Loader2 className="animate-spin text-slate-400" /></div>}
      {error && <p role="alert" className="text-sm text-amber-700" data-testid="event-file-preview-error">{error}</p>}
      {source && <div className="min-w-0" data-testid="event-file-preview-content">
        {mime.startsWith("image/") && <img src={source} alt={attachment.filename} className="w-full max-h-[65dvh] object-contain" data-testid="event-file-image" />}
        {mime === "application/pdf" && <Suspense fallback={<div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>}><PdfAttachmentView source={source} /></Suspense>}
        {mime.startsWith("video/") && <video src={source} controls playsInline preload="metadata" className="w-full max-h-[60dvh] aspect-video object-contain bg-black rounded-md" onError={() => setError("Ce navigateur ne peut pas lire ce format vidéo. Vous pouvez télécharger le fichier.")} data-testid="event-file-video" />}
        {mime.startsWith("audio/") && <audio src={source} controls preload="metadata" className="w-full min-w-0" onError={() => setError("Ce navigateur ne peut pas lire ce format audio. Vous pouvez télécharger le fichier.")} data-testid="event-file-audio" />}
      </div>}
      <div className="flex flex-wrap gap-2 justify-end">
        {source && mime === "application/pdf" && <Button asChild variant="outline" className="min-h-11"><a href={source} target="_blank" rel="noopener noreferrer" data-testid="event-file-open-pdf"><ExternalLink size={15} className="mr-2" />Ouvrir le PDF</a></Button>}
        <Button onClick={download} disabled={downloading} className="min-h-11" data-testid="event-file-download">{downloading ? <Loader2 size={15} className="animate-spin mr-2" /> : <Download size={15} className="mr-2" />}Télécharger</Button>
      </div>
    </DialogContent>
  </Dialog>;
};